import { UUID } from '../utils/uuid';
import { getChatAuthHeaders } from './chat-auth-service';

export type ChatHistoryMessage = {
  role: 'user' | 'assistant';
  content: string;
};

export type ModelType = 'BSET' | 'PHSET' | 'MASCORE' | 'VBAT' | 'HSET' | (string & {});

export type ChatModelAssociation = {
  modelName?: string | null;
  draftId?: string | null;
  modelType?: ModelType | string | null;
  modelKey?: string | null;
};

export type ChatSessionSummary = {
  uuid: UUID;
  sessionName: string;
  timestamp: number;
  isPinned: boolean;
  modelTypes?: ModelType[] | null;
  modelName?: string | null;
  draftId?: string | null;
  modelType?: ModelType | string | null;
  modelKey?: string | null;
};

export type ChatSessionUpdatePayload = {
  sessionName?: string;
  isPinned?: boolean;
  modelTypes?: ModelType[] | null;
  modelName?: string | null;
  draftId?: string | null;
  modelType?: ModelType | string | null;
  modelKey?: string | null;
};

export type ChatSessionState = Record<string, unknown>;

export interface IChatHistoryService {
  fetchSessions(modelAssociation?: ChatModelAssociation | null): Promise<ChatSessionSummary[]>;
  fetchChatHistory(
    sessionId: UUID,
    limit?: number,
    offset?: number
  ): Promise<ChatHistoryMessage[]>;
  fetchSessionState(sessionId: UUID): Promise<ChatSessionState | null>;
  saveSessionState(sessionId: UUID, state: ChatSessionState): Promise<void>;
  updateSession(sessionId: UUID, updates: ChatSessionUpdatePayload): Promise<ChatSessionSummary>;
  deleteSession(sessionId: UUID): Promise<void>;
}

const DEFAULT_BASE_URL = process.env.REACT_APP_ESET_COMPLETION_BASE_URL ?? '/';

export class ESETHistoryService implements IChatHistoryService {
  private readonly baseUrl: string;

  constructor(baseUrl: string = DEFAULT_BASE_URL) {
    this.baseUrl = this.normalizeBaseUrl(baseUrl);
  }

  private normalizeBaseUrl(candidate: string): string {
    const value = candidate?.trim() || '/';

    const isAbsolute = /^https?:\/\//i.test(value);
    if (isAbsolute) {
      return value;
    }

    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';

    return value.startsWith('/') ? `${origin}${value}` : `${origin}/${value}`;
  }

  private buildUrl(path: string, params: Record<string, string | number | null | undefined>): string {
    const url = new URL(path, this.baseUrl);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    });
    return url.toString();
  }

  private normalizeSession(item: any): ChatSessionSummary {
    return {
      uuid: item.uuid as UUID,
      sessionName: item.session_name ?? item.sessionName ?? 'Untitled chat',
      timestamp: Number(item.timestamp) || 0,
      isPinned: Boolean(item.is_pinned ?? item.isPinned),
      modelTypes: item.model_types ?? item.modelTypes ?? null,
      modelName: item.model_name ?? item.modelName ?? null,
      draftId: item.draft_id ?? item.draftId ?? null,
      modelType: item.model_type ?? item.modelType ?? null,
      modelKey: item.model_key ?? item.modelKey ?? null,
    };
  }

  async fetchSessions(modelAssociation?: ChatModelAssociation | null): Promise<ChatSessionSummary[]> {
    const authHeaders = await getChatAuthHeaders();
    const url = this.buildUrl('/chat/sessions', {
      model_key: modelAssociation?.modelKey || undefined,
      model_name: modelAssociation?.modelName || undefined,
      draft_id: modelAssociation?.draftId || undefined,
    });

    const response = await fetch(url, {
      headers: authHeaders,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('ESET chat history sessions request failed:', response.status, errorText);
      throw new Error(`Unable to fetch chat sessions (${response.status}).`);
    }

    const payload = await response.json();

    if (!Array.isArray(payload)) {
      return [];
    }

    return payload.map(item => this.normalizeSession(item));
  }

  async fetchChatHistory(sessionId: UUID, limit: number = 200, offset: number = 0): Promise<ChatHistoryMessage[]> {
    const authHeaders = await getChatAuthHeaders();
    const response = await fetch(
      this.buildUrl('/chat', {
        session_id: sessionId,
        limit,
        offset,
      }),
      {
        headers: authHeaders,
      }
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('ESET chat history request failed:', response.status, errorText);
      throw new Error(`Unable to fetch chat history (${response.status}).`);
    }

    const payload = await response.json();

    if (!Array.isArray(payload)) {
      return [];
    }

    return payload
      .filter(item => item?.role === 'user' || item?.role === 'assistant')
      .map((item: any) => ({
        role: item.role,
        content: typeof item.content === 'string' ? item.content : JSON.stringify(item.content ?? ''),
      }));
  }

  async fetchSessionState(sessionId: UUID): Promise<ChatSessionState | null> {
    const authHeaders = await getChatAuthHeaders();
    const response = await fetch(
      this.buildUrl(`/chat/sessions/${sessionId}/state`, {}),
      {
        headers: authHeaders,
      }
    );

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('ESET chat session state request failed:', response.status, errorText);
      throw new Error(`Unable to fetch chat session state (${response.status}).`);
    }

    const payload = await response.json();

    if (payload && typeof payload === 'object' && 'state' in payload) {
      return (payload as { state: ChatSessionState }).state ?? null;
    }

    return payload ?? null;
  }

  async saveSessionState(sessionId: UUID, state: ChatSessionState): Promise<void> {
    const authHeaders = await getChatAuthHeaders();
    const response = await fetch(
      this.buildUrl(`/chat/sessions/${sessionId}/state`, {}),
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify({ state }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('ESET chat session state save failed:', response.status, errorText);
      throw new Error(`Unable to save chat session state (${response.status}).`);
    }
  }

  async updateSession(sessionId: UUID, updates: ChatSessionUpdatePayload): Promise<ChatSessionSummary> {
    const authHeaders = await getChatAuthHeaders();
    const response = await fetch(this.buildUrl(`/chat/sessions/${sessionId}`, {}), {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({
        session_name: updates.sessionName,
        is_pinned: updates.isPinned,
        model_types: updates.modelTypes,
        model_name: updates.modelName,
        draft_id: updates.draftId,
        model_type: updates.modelType,
        model_key: updates.modelKey,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('ESET chat history update request failed:', response.status, errorText);
      throw new Error(`Unable to update chat session (${response.status}).`);
    }

    const payload = await response.json();
    return this.normalizeSession(payload);
  }

  async deleteSession(sessionId: UUID): Promise<void> {
    const authHeaders = await getChatAuthHeaders();
    const response = await fetch(this.buildUrl(`/chat/sessions/${sessionId}`, {}), {
      method: 'DELETE',
      headers: authHeaders,
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      console.error('ESET chat history delete request failed:', response.status, errorText);
      throw new Error(`Unable to delete chat session (${response.status}).`);
    }
  }
}
